import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router, UrlTree } from '@angular/router';
import {
  BoelsBaseSite,
  BoelsBaseSiteEntities,
  BoelsBaseSiteLanguage,
} from '@boels/models';
import { CmsFacade, SiteContextFacade } from '@boels/shared-state';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { TranslocoModule, TRANSLOCO_SCOPE } from '@jsverse/transloco';
import { map, Observable } from 'rxjs';
import { LanguageSelectorModalItemComponent } from './lanaguage-selector-modal-item/language-selector-modal-item.component';
import { ModalService, WindowRef } from '@boels/helpers';

@Component({
  selector: 'boels-language-selector-modal',
  templateUrl: './language-selector-modal.component.html',
  styleUrls: ['./language-selector-modal.component.scss'],
  imports: [
    LanguageSelectorModalItemComponent,
    FontAwesomeModule,
    AsyncPipe,
    TranslocoModule,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['header'],
    },
  ],
})
export class LanguageSelectorModalComponent implements OnInit {
  @ViewChild('closeButton') closeButton: ElementRef;
  modalService = inject(ModalService);
  private readonly siteContextFacade = inject(SiteContextFacade);
  private cmsFacade = inject(CmsFacade);
  private router = inject(Router);
  private winRef = inject(WindowRef);

  faClose = faXmark;
  baseSites$: Observable<BoelsBaseSite[]>;

  ngOnInit(): void {
    this.baseSites$ = this.siteContextFacade.getBaseSites().pipe(
      map((boelsBaseSites: BoelsBaseSiteEntities) =>
        Object.values(boelsBaseSites)
          .filter((boelsBaseSite: BoelsBaseSite) => boelsBaseSite.country)
          .sort(
            (actualBoelsBaseSite, nextBoelsBaseSite) =>
              actualBoelsBaseSite.country?.nativeName?.localeCompare(
                nextBoelsBaseSite.country?.nativeName as string,
              ) as number,
          ),
      ),
    );
  }

  async setBoelsBaseSiteLanguage(
    boelsBaseSiteLanguage?: BoelsBaseSiteLanguage,
  ): Promise<void> {
    const languageIsoCode = boelsBaseSiteLanguage?.language?.isocode as string;
    const countryIsoCode = boelsBaseSiteLanguage?.baseSite?.country
      ?.isocode as string;

    // Close the modal
    this.modalService.close();

    // Then update the culture
    if (languageIsoCode && countryIsoCode) {
      await this.buildCulture(languageIsoCode, countryIsoCode);
    }
  }

  closeModal(): void {
    this.modalService.close();
  }

  async buildCulture(
    languageIsoCode: string,
    countryIsoCode: string,
  ): Promise<void> {
    const language = languageIsoCode.split('_')[0];
    const culture = `${language}-${countryIsoCode?.toLowerCase()}`;
    const currentCountry = this.router.url.slice(4, 6);

    const urlSegments = this.router.url.split('/');

    if (
      currentCountry !== countryIsoCode.toLocaleLowerCase() &&
      (urlSegments.includes('cart') || urlSegments.includes('checkout'))
    ) {
      this.winRef.replaceWindowLocationForCulture(culture, true);
      return;
    }

    let urlTree: UrlTree | string;
    const urlParts = urlSegments.map((param, index) =>
      index === 1 ? culture : param,
    );

    if (urlParts[urlParts.length - 1] === 'not-found') {
      const previous = await this.cmsFacade.getPreviousPage();
      urlParts.pop();
      if (previous) urlParts.push(previous);
    }
    const url = urlParts.join('/');

    if (url.indexOf('?') !== -1) {
      urlTree = this.constructUrlTreeWithQueryParams(url);
    } else {
      urlTree = url;
    }

    this.router.navigateByUrl(urlTree);
  }

  trackByUid(index: number, boelsBaseSite: BoelsBaseSite): string {
    return boelsBaseSite.uid as string;
  }

  private constructUrlTreeWithQueryParams(url: string): UrlTree {
    const urlPart: string = url.substring(1, url.indexOf('?'));
    const queryParamsString: string = url.substring(
      url.indexOf('?') + 1,
      url.length,
    );
    const queryParamsObject =
      this.convertQueryParamsStringToObject(queryParamsString);
    return this.router.createUrlTree([urlPart], {
      queryParams: queryParamsObject,
    });
  }

  private convertQueryParamsStringToObject(queryParamsString: string) {
    return queryParamsString.split('&').reduce((acc, item) => {
      const keyValue = item.split('=');
      return { ...acc, [keyValue[0]]: keyValue[1] };
    }, {});
  }
}
